<template>
  <div class="learning-platform-page">
    <!-- 1. Top Banner Section -->
    <div class="top-banner">
      <div class="banner-content">
        <h1 class="banner-title">{{ bannerTitle }}</h1>
        <p class="banner-desc">
          {{ bannerDescLine1 }}<br>
          {{ bannerDescLine2 }}
        </p>
        <div class="banner-img-container">
          <img :src="bannerImage"
               :alt="bannerImageAlt"
               class="banner-main-img" />
          <!-- Optional floating elements based on UI screenshot if needed, sticking to basics first -->
        </div>
      </div>
    </div>

    <!-- 2. Explore Section -->
    <div class="explore-section">
      <div class="section-container">
        <h2 class="section-title">{{ exploreTitle }}</h2>

        <!-- Tabs -->
        <div class="explore-tabs">
          <div class="tab-item"
               v-for="(tab, index) in exploreTabs"
               :key="index"
               @click="currentTab = index"
               :class="{ active: currentTab === index }">
            <div class="tab-icon-wrapper">
              <img :src="tab.icon"
                   :alt="tab.name"
                   class="tab-icon" />
            </div>
            <span class="tab-name">{{ tab.name }}</span>
          </div>
        </div>

        <!-- Tab Content -->
        <div class="tab-content-wrapper">
          <transition name="fade"
                      mode="out-in">
            <img :key="currentTab"
                 :src="exploreTabs[currentTab].contentImage"
                 :alt="tabContentAlt"
                 class="tab-content-img" />
          </transition>
        </div>
      </div>
    </div>

    <!-- 3. Learning Platform Intro Section -->
    <div class="platform-intro-section">
      <div class="section-container">
        <h2 class="section-title">{{ introTitle }}</h2>

        <div class="platform-intro-content">
          <div class="intro-text-side">
            <div class="intro-item"
                 v-for="(item, index) in introItems"
                 :key="index">
              <h3 class="intro-item-title">{{ item.title }}</h3>
              <p class="intro-item-desc">
                {{ item.desc }}
              </p>
            </div>
          </div>
          <div class="intro-image-side">
            <img :src="introImage"
                 :alt="introImageAlt"
                 class="intro-side-img" />
          </div>
        </div>
      </div>
    </div>

    <!-- 4. Middle Banner Section (Gallery) -->
    <div class="gallery-banner-section">
      <div class="gallery-single">
        <img :src="galleryImage"
             :alt="galleryAlt"
             class="gallery-img" />
      </div>
    </div>

    <!-- 5. Download Section -->
    <div class="download-section">
      <div class="section-container">
        <h2 class="section-title">{{ downloadTitle }}</h2>
        <div class="download-list">
          <div class="download-item"
               v-for="(app, index) in downloadApps"
               :key="index">
            <div class="app-icon-wrapper">
              <img :src="app.icon"
                   :alt="app.name"
                   class="app-icon" />
            </div>
            <div class="app-name">{{ app.name }}</div>
            <button class="download-btn"
                    @click="openUrl(app.url)">{{ downloadButtonText }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { softTableApi } from '@/api'

export default {
  name: 'LearningPlatform',
  data() {
    return {
      currentTab: 0,
      softLoaded: false,
      softItems: [],
      bannerImage: require('@/assets/ruanjian/banner.png'),
      introImage: require('@/assets/ruanjian/learn.png'),
      galleryImage: require('@/assets/ruanjian/bottom-banner.png'),
      exploreTabAssets: [
        {
          icon: require('@/assets/ruanjian/icon1.png'),
          contentImage: require('@/assets/ruanjian/icon-1.png')
        },
        {
          icon: require('@/assets/ruanjian/icon2.png'),
          contentImage: require('@/assets/ruanjian/icon-2.png')
        },
        {
          icon: require('@/assets/ruanjian/icon3.png'),
          contentImage: require('@/assets/ruanjian/icon-3.png')
        },
        {
          icon: require('@/assets/ruanjian/icon4.png'),
          contentImage: require('@/assets/ruanjian/icon-4.png')
        },
        {
          icon: require('@/assets/ruanjian/icon5.png'),
          contentImage: require('@/assets/ruanjian/icon-5.png')
        }
      ],
      downloadAppAssets: [
        {
          icon: 'https://cdn-icons-png.flaticon.com/512/732/732200.png'
        },
        {
          icon: 'https://cdn-icons-png.flaticon.com/512/888/888859.png'
        }
      ]
    }
  },
  mounted() {
    this.loadSoftTable()
  },
  computed: {
    learningPlatformCopy() {
      const locale = this.$i18n && this.$i18n.locale ? this.$i18n.locale : 'zh-CN'
      const messages =
        this.$i18n && this.$i18n.getLocaleMessage
          ? this.$i18n.getLocaleMessage(locale)
          : {}
      return (messages.software && messages.software.learningPlatform) || {}
    },
    bannerTitle() {
      return this.learningPlatformCopy.bannerTitle || ''
    },
    bannerDescLine1() {
      return this.learningPlatformCopy.bannerDescLine1 || ''
    },
    bannerDescLine2() {
      return this.learningPlatformCopy.bannerDescLine2 || ''
    },
    bannerImageAlt() {
      return this.learningPlatformCopy.bannerImageAlt || ''
    },
    exploreTitle() {
      return this.learningPlatformCopy.exploreTitle || ''
    },
    exploreTabs() {
      const tabs = this.learningPlatformCopy.exploreTabs || []
      return tabs.map((item, index) => {
        const asset = this.exploreTabAssets[index] || {}
        return {
          name: item.name || '',
          icon: asset.icon || '',
          contentImage: asset.contentImage || ''
        }
      })
    },
    tabContentAlt() {
      return this.learningPlatformCopy.tabContentAlt || ''
    },
    introTitle() {
      return this.learningPlatformCopy.introTitle || ''
    },
    introItems() {
      return this.learningPlatformCopy.introItems || []
    },
    introImageAlt() {
      return this.learningPlatformCopy.introImageAlt || ''
    },
    galleryAlt() {
      return this.learningPlatformCopy.galleryAlt || ''
    },
    downloadTitle() {
      return this.learningPlatformCopy.downloadTitle || ''
    },
    downloadApps() {
      // 获取配置中期望的软件名称列表
      const expectedApps = this.learningPlatformCopy.downloadApps || []
      
      if (this.softLoaded) {
        // 对每个期望的软件，从服务器数据中查找匹配项（按照 DownloadCenter 的简单逻辑）
        return expectedApps.map((expectedApp, index) => {
          const asset = this.downloadAppAssets[index] || {}
          const expectedName = expectedApp.name || ''
          
          // 在服务器数据中查找匹配的软件（先按设备类型过滤，再按平台关键词匹配）
          // 确定配置期望的设备类型
          const configName = String(expectedName || '').toLowerCase()
          const configHasApp = configName.includes('app') || configName.includes('手机') || configName.includes('mobile')
          const configHasPC = configName.includes('pc') || configName.includes('pc版') || configName.includes('电脑')
          
          // 先按设备类型过滤服务器数据
          const filteredItems = this.softItems.filter((item) => {
            if (!item || !item.name) return false
            
            const serverName = String(item.name || '').toLowerCase()
            const serverHasApp = serverName.includes('app') || serverName.includes('手机') || serverName.includes('mobile')
            const serverHasPC = serverName.includes('pc') || serverName.includes('pc端') || serverName.includes('电脑')
            
            // 如果配置期望App，只匹配App类型的服务器项
            if (configHasApp && !configHasPC) {
              return serverHasApp && !serverHasPC
            }
            // 如果配置期望PC，只匹配PC类型的服务器项
            if (configHasPC && !configHasApp) {
              return serverHasPC && !serverHasApp
            }
            // 如果配置没有明确设备类型，匹配所有
            return true
          })
          
          // 在过滤后的数据中查找匹配项
          const matchedItem = filteredItems.find((item) => {
            if (!item || !item.name) return false
            
            const serverName = String(item.name || '').toLowerCase()
            
            // 标准化名称（去除空格、标点、连字符等）
            const normalizedServer = serverName.replace(/[`"''"、，,。.\s_-]+/g, '')
            const normalizedConfig = configName.replace(/[`"''"、，,。.\s_-]+/g, '')
            
            // 1. 完全匹配或包含匹配
            if (normalizedServer === normalizedConfig || 
                normalizedServer.includes(normalizedConfig) || 
                normalizedConfig.includes(normalizedServer)) {
              return true
            }
            
            // 2. 提取平台相关关键词（更宽松的匹配）
            const serverHasPlatform = serverName.includes('平台') || serverName.includes('platform') || 
                                     serverName.includes('学习') || serverName.includes('learning') || 
                                     serverName.includes('科教') || serverName.includes('激光') || serverName.includes('laser')
            const configHasPlatform = configName.includes('平台') || configName.includes('platform') || 
                                     configName.includes('学习') || configName.includes('learning') || 
                                     configName.includes('科教') || configName.includes('设计')
            
            // 3. 如果都包含平台相关关键词，就匹配
            if (serverHasPlatform && configHasPlatform) {
              return true
            }
            
            // 4. 如果服务器有平台关键词，配置有设计/软件/科教相关，也匹配
            if (serverHasPlatform && (configHasPlatform || configName.includes('设计') || configName.includes('软件'))) {
              return true
            }
            
            return false
          })
          
          if (matchedItem) {
            // 找到匹配项，使用服务器数据
            return {
              id: matchedItem.id,
              name: String(matchedItem.name || ''),
              icon: this.normalizeUrl(matchedItem.photoUrl) || asset.icon || '',
              url: this.normalizeUrl(matchedItem.url),
              version: matchedItem.version || '',
              system: matchedItem.system || '',
              type: matchedItem.type || '',
              createTime: matchedItem.createTime || '',
            }
          } else {
            // 未找到匹配项，使用配置数据
            return {
              name: expectedName,
              icon: asset.icon || '',
              url: ''
            }
          }
        })
      }

      // 如果服务器数据未加载，返回配置数据
      return expectedApps.map((item, index) => {
        const asset = this.downloadAppAssets[index] || {}
        return {
          name: item.name || '',
          icon: asset.icon || '',
          url: ''
        }
      })
    },
    downloadButtonText() {
      return this.learningPlatformCopy.downloadButtonText || ''
    }
  },
  methods: {
    normalizeUrl(url) {
      if (!url) return ''
      const str = String(url).trim()
      if (!str) return ''
      const noBackticks = str.replace(/`/g, '').trim()
      return noBackticks.replace(/^["']+|["']+$/g, '').trim()
    },
    openUrl(url) {
      const cleaned = this.normalizeUrl(url)
      if (!cleaned) {
        console.warn('下载链接为空，无法下载')
        // 可以添加用户提示，比如 alert 或 toast
        return
      }
      window.open(encodeURI(cleaned), '_blank')
    },
    async loadSoftTable() {
      try {
        const result = await softTableApi.selectAll()
        const ok =
          result &&
          (result.code === 200 ||
            result.success === true ||
            result.ok === true)
        const list = ok && Array.isArray(result.data) ? result.data : []
        this.softItems = list
        this.softLoaded = true
      } catch (e) {
        this.softLoaded = false
        this.softItems = []
      }
    },
  },
}
</script>

<style scoped>
.learning-platform-page {
  width: 100%;
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
  overflow-x: hidden;
}

.section-container {
  width: 1200px;
  margin: 0 auto;
}

/* 1. Top Banner Section */
.top-banner {
  width: 100%;
  height: 880px;
  background-color: #2ea2fa; /* Fallback */
  background: linear-gradient(135deg, #2ea2fa 0%, #1a82d6 100%);
  position: relative;
  text-align: center;
  padding-top: 80px; /* Adjust padding */
}

.banner-content {
  width: 1200px;
  margin: 0 auto;
  position: relative;
  height: 100%;
}

.banner-title {
  font-size: 48px;
  color: #fff;
  font-weight: bold;
  margin-bottom: 30px;
}

.banner-desc {
  font-size: 24px;
  color: #fff;
  line-height: 1.5;
  margin-bottom: 60px;
}

.banner-img-container {
  width: 1200px;
  height: 686px;
  position: absolute;
  left: 0;
  bottom: -134px; /* Overflow by 134px */
  z-index: 10;

  border-radius: 20px;
  background: #fff; /* Fallback */
}

.banner-main-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 20px;
}

/* 2. Explore Section */
.explore-section {
  width: 100%;
  height: 1078px;
  background-color: #f5fbff;
  padding-top: 200px; /* 134px gap + padding for spacing */
  /* User said "distance from top is 134px". Since the image overflows 134px, 
     we need to push the content down. 
     The gap is 134px visual space BELOW the image? Or from the banner bottom?
     If image overflows banner by 134px, it sits ON TOP of explore section.
     So Explore section top padding should be at least 134px + some spacing.
     Let's use padding-top: 200px to give room for the floating image and title space.
  */
}

.section-title {
  font-size: 34px;
  color: #333;
  font-weight: bold;
  text-align: center;
  margin-bottom: 60px;
}

.explore-tabs {
  width: 1200px;
  height: 209px;
  display: flex;
  justify-content: space-between;
  margin-bottom: 50px;
}

.tab-item {
  width: 210px;
  height: 209px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 32px;
  transition: all 0.3s ease;
}

.tab-item:hover {
  background-color: rgba(255, 255, 255, 0.5);
}

.tab-item.active {
  background-color: #fff;
}

.tab-icon-wrapper {
  width: 74px;
  height: 74px;
  margin-bottom: 20px;
}

.tab-icon {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.tab-name {
  font-size: 20px;
  color: #333;
}

.tab-content-wrapper {
  width: 922px;
  height: 535px;
  margin: 0 auto;
  border-radius: 20px;
  overflow: hidden;
}

.tab-content-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 3. Platform Intro Section */
.platform-intro-section {
  width: 100%;
  height: 843px;
  background-color: #fff;
  padding-top: 80px;
}

.platform-intro-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 60px;
}

.intro-text-side {
  width: 500px; /* Adjust to fit layout */
}

.intro-item {
  margin-bottom: 50px;
}

.intro-item-title {
  font-size: 20px;
  font-weight: bold;
  color: #333;
  margin-bottom: 15px;
}

.intro-item-desc {
  font-size: 20px;
  font-weight: normal; /* "不加粗" */
  color: #333;
  line-height: 1.6;
}

.intro-image-side {
  width: 600px;
  height: 509px;
}

.intro-side-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 20px;
}

/* 4. Gallery Banner Section */
.gallery-banner-section {
  width: 100%;
  height: 573px;
  background-color: #333; /* Placeholder for images */
  overflow: hidden;
}

.gallery-single {
  width: 100%;
  height: 100%;
}

.gallery-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 5. Download Section */
.download-section {
  width: 100%;
  height: 493px;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 1;
}

.download-list {
  display: flex;
  justify-content: center;
  gap: 100px;
  margin-top: 60px;
  position: relative;
  z-index: 2;
}

.download-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
}

.app-icon-wrapper {
  width: 80px; /* Estimated */
  height: 80px;
  margin-bottom: 20px;
}

.app-icon {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.app-name {
  font-size: 16px;
  color: #333;
  margin-bottom: 25px;
}

.download-btn {
  width: 126px;
  height: 41px;
  background-color: #2ea2fa;
  border-radius: 21px;
  border: none;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s;
  position: relative;
  z-index: 10;
  pointer-events: auto;
}

.download-btn:hover {
  background-color: #258cd6;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
